import React, { useState } from "react";
import { X, Copy, Check, Link as LinkIcon, ShieldCheck, Code } from "lucide-react";

export default function ShareModal({
  open,
  shareUrl,
  shareCopied,
  onClose,
  onCopy,
  onRevoke,
}) {
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);

  if (!open) return null;

  // Derive backend origin + token to build the embeddable SVG snippet.
  // Pattern: <FRONTEND_URL>/share/<token>
  const backendOrigin =
    (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const tokenMatch = (shareUrl || "").match(/\/share\/([A-Za-z0-9_-]+)/);
  const shareToken = tokenMatch ? tokenMatch[1] : "";
  const badgeSvgUrl = shareToken
    ? `${backendOrigin}/api/public/share/${shareToken}/badge.svg`
    : "";
  const embedSnippet = shareToken
    ? `<a href="${shareUrl}" target="_blank" rel="noreferrer"><img src="${badgeSvgUrl}" alt="Verified by Resmo" height="72" /></a>`
    : "";

  const copyEmbed = async () => {
    if (!embedSnippet) return;
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 1800);
    } catch (_e) {
      /* clipboard blocked — fail silently */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 27, 45, 0.55)" }}
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="rm-card-static p-6 md:p-8 w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#FFF2EC" }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="rm-tag mb-2">
              <LinkIcon size={12} /> Share
            </div>
            <h2 className="font-display text-3xl uppercase leading-tight">
              Read-only <span className="rm-text-orange">fit report.</span>
            </h2>
            <p className="text-sm mt-2" style={{ color: "#475569" }}>
              Anyone with this link can see your fit matrix, company tone insight, and tailored
              resume preview. Job description and personal data are stripped.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rm-btn rm-btn-ghost text-xs px-2 py-2"
            data-testid="share-close-btn"
          >
            <X size={14} />
          </button>
        </div>

        {/* Share URL row */}
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className="rm-input flex-1"
            data-testid="share-url-input"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={onCopy}
            className="rm-btn rm-btn-primary text-xs px-3 py-2"
            data-testid="share-copy-btn"
          >
            {shareCopied ? (
              <>
                <Check size={14} /> Copied
              </>
            ) : (
              <>
                <Copy size={14} /> Copy
              </>
            )}
          </button>
        </div>

        {/* Embeddable "Verified by Resmo" badge snippet */}
        {shareToken && (
          <div
            className="mt-4 rm-card-static p-3 md:p-4"
            style={{ background: "#ECFDF5", borderColor: "#047857" }}
            data-testid="share-embed-block"
          >
            <button
              type="button"
              onClick={() => setShowEmbed((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="share-embed-toggle"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} style={{ color: "#047857" }} />
                <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                      style={{ color: "#047857", fontWeight: 700 }}>
                  · Embed the "Verified by Resmo" badge
                </span>
              </div>
              <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                    style={{ color: "#047857" }}>
                {showEmbed ? "Hide" : "Show snippet"}
              </span>
            </button>

            {showEmbed && (
              <div className="mt-3" data-testid="share-embed-body">
                {/* Live badge preview */}
                <div className="mb-3 flex justify-center p-3"
                     style={{ background: "white", border: "2px dashed #047857", borderRadius: 8 }}>
                  <img
                    src={badgeSvgUrl}
                    alt="Verified by Resmo badge preview"
                    style={{ height: 72 }}
                    data-testid="share-embed-preview"
                  />
                </div>
                <p className="text-[12px] leading-snug mb-2" style={{ color: "#064E3B" }}>
                  Paste this HTML snippet anywhere you can render HTML — your
                  personal site, GitHub README, email signature — and the badge
                  updates live every time you re-review your honesty check.
                </p>
                <div className="flex gap-2">
                  <textarea
                    readOnly
                    value={embedSnippet}
                    rows={3}
                    className="rm-input flex-1 font-mono-rm text-[11px]"
                    onFocus={(e) => e.target.select()}
                    data-testid="share-embed-snippet"
                  />
                  <button
                    type="button"
                    onClick={copyEmbed}
                    className="rm-btn rm-btn-primary text-xs px-3 py-2"
                    data-testid="share-embed-copy-btn"
                  >
                    {embedCopied ? (
                      <>
                        <Check size={14} /> Copied
                      </>
                    ) : (
                      <>
                        <Code size={14} /> Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[10px] mt-2 font-mono-rm uppercase tracking-widest"
                   style={{ color: "#047857" }}>
                  · LinkedIn tip: download the badge SVG and attach as an image to a post
                </p>
                <a
                  href={badgeSvgUrl}
                  download={`resmo-verified-${shareToken.slice(0, 8)}.svg`}
                  className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 mt-2 inline-flex"
                  data-testid="share-embed-download-btn"
                >
                  <ShieldCheck size={11} /> Download SVG
                </a>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono-rm text-xs uppercase tracking-widest underline"
            style={{ color: "#FF6A00" }}
            data-testid="share-preview-link"
          >
            Preview ↗
          </a>
          <button
            onClick={onRevoke}
            className="rm-btn rm-btn-ghost text-xs"
            data-testid="share-revoke-btn"
          >
            Revoke link
          </button>
        </div>
      </div>
    </div>
  );
}
